import { useMemo, useState } from 'react';
import {
  Badge,
  EmptyState,
  Metric,
  PaginatedTable,
  Panel,
  Progress,
  Select,
  fetchContractors,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatPercentage,
  searchContractors,
  type BadgeTone,
  type RawCellValue,
  type RemoteOption,
  type RemoteSearch,
  type TableColumn,
  type TableSelectSource,
} from 'escrowbuild-ui';

import { useFixtureVocabulary, useT, type AppTranslate, type FixtureVocabulary } from '@/i18n';
import { CONTRACTORS, CONTRACTOR_OPTIONS, EXPENSES, findProject } from '@/data/domain';

/* =============================================================================
   Suppliers and partners — the document's §9
   -----------------------------------------------------------------------------
   Two layers of money meet on this screen and they are not the same number. The
   register carries the AWARD the platform holds for a supplier (contract value,
   paid to date), and the ledger carries the SPEND actually recorded against one,
   keyed by `vendorId`. A page that showed only one of them would let a client
   read an award as though it were a receipt, so both are below and the panel
   says which is which.
   ============================================================================= */

/** Contractors carry money in dollars, completion as a whole percent. */
const asMoney = (value: RawCellValue): string =>
  typeof value === 'number' ? formatMoney(value, { precision: 0 }) : '';
const asPercent1 = (value: RawCellValue): string =>
  typeof value === 'number' ? formatPercentage(value, { precision: 1 }) : '';
const asCount = (value: RawCellValue): string =>
  typeof value === 'number' ? formatNumber(value) : '';
const asDay = (value: RawCellValue): string =>
  typeof value === 'string' ? formatDate(value, { style: 'medium' }) : '';
const asStamp = (value: RawCellValue): string =>
  typeof value === 'string' ? formatDateTime(value) : '';

/**
 * Built from `t` rather than held at module scope, because a header is
 * interface text. The component memoises the result on `t`, which is what keeps
 * the array identity the table memoises its own column model on — an inline
 * array would rebuild `aggregations` on every render.
 */
function tableColumns(t: AppTranslate, vocabulary: FixtureVocabulary): TableColumn[] {
  const asFlag = (value: RawCellValue): string =>
    value === true ? t('contractors.yes') : t('contractors.no');

  return [
    { key: 'id', header: t('contractors.contractId'), width: 118, sortable: true, filterable: true, filterType: 'text' },
    { key: 'name', header: t('contractors.supplier'), width: 208, sortable: true, filterable: true, filterType: 'text' },
    { key: 'trade', header: t('contractors.trade'), width: 150, sortable: true, filterable: true, filterType: 'select', formatter: asVocabulary(vocabulary.trade) },
    { key: 'phase', header: t('contractors.phase'), width: 248, sortable: true, filterable: true, filterType: 'select', formatter: asVocabulary(vocabulary.phase) },
    { key: 'status', header: t('common.status'), width: 132, sortable: true, filterable: true, filterType: 'select', formatter: asVocabulary(vocabulary.contractorStatus) },
    {
      key: 'contractValue',
      header: t('contractors.contractValue'),
      width: 158,
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    {
      key: 'paidToDate',
      header: t('contractors.paidToDate'),
      width: 148,
      sortable: true,
      filterable: true,
      filterType: 'moneyRange',
      formatter: asMoney,
      aggregate: { op: 'sum' },
    },
    {
      key: 'completion',
      header: t('contractors.completion'),
      width: 138,
      sortable: true,
      filterable: true,
      filterType: 'percentageRange',
      formatter: asPercent1,
      aggregate: { op: 'avg', precision: 1 },
    },
    {
      key: 'crewSize',
      header: t('contractors.crew'),
      width: 104,
      sortable: true,
      filterable: true,
      filterType: 'numberRange',
      formatter: asCount,
      aggregate: { op: 'sum' },
    },
    {
      key: 'startDate',
      header: t('contractors.started'),
      width: 148,
      sortable: true,
      filterable: true,
      filterType: 'date',
      formatter: asDay,
    },
    {
      key: 'lastInspection',
      header: t('contractors.lastInspection'),
      width: 172,
      sortable: true,
      filterable: true,
      filterType: 'date',
      formatter: asDay,
    },
    {
      key: 'approvedAt',
      header: t('contractors.approvedAt'),
      width: 190,
      sortable: true,
      filterable: true,
      filterType: 'datetime',
      formatter: asStamp,
    },
    {
      key: 'isVerified',
      header: t('contractors.verified'),
      width: 112,
      align: 'center',
      sortable: true,
      formatter: asFlag,
    },
  ];
}

/** Cancellable pause, so a superseded trade search never resolves into a newer one. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * A cell formatter for one of the fixture's closed vocabularies: the cell holds
 * the register's own token and the word a reader sees is resolved from it, so
 * the sort and the filter keep working on the value while the display follows
 * the language.
 */
const asVocabulary =
  (resolve: (value: string) => string) =>
  (value: RawCellValue): string =>
    typeof value === 'string' ? resolve(value) : '';

/**
 * A `select` filter over one of the register's closed vocabularies — only over
 * the values the fixture actually holds, so the popover can never offer a value
 * that matches nothing, which is the membership contract of §11.
 *
 * Built per locale rather than held at module scope: an option's LABEL is the
 * word the reader is shown, while its VALUE stays the fixture's own token, which
 * is what the register's cells hold and therefore what the filter matches. The
 * columns are `select` rather than free text for the same reason — a text filter
 * would ask the reader to type the English the cells no longer show.
 */
function vocabularySearch(values: string[], label: (value: string) => string): RemoteSearch {
  const options: RemoteOption[] = [...new Set(values)]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: label(value) }));

  return async (term, signal) => {
    await pause(140, signal);
    const needle = term.trim().toLowerCase();
    const matched =
      needle === ''
        ? options
        : options.filter((option) => option.label.toLowerCase().includes(needle));
    return { options: matched, truncated: false };
  };
}

/** The register's filter popovers, built per locale: their placeholders are
 *  interface text and their options are the fixture's own values, each in the
 *  reader's words. */
function buildSelectSources(
  t: AppTranslate,
  vocabulary: FixtureVocabulary,
): Record<string, TableSelectSource> {
  const over = (pick: (row: (typeof CONTRACTORS)[number]) => string) =>
    CONTRACTORS.map(pick);

  return {
    trade: {
      search: vocabularySearch(over((row) => row.trade), vocabulary.trade),
      minSearchLength: 1,
      placeholder: t('contractors.register.searchTrades'),
    },
    phase: {
      search: vocabularySearch(over((row) => row.phase), vocabulary.phase),
      minSearchLength: 1,
      placeholder: t('contractors.phase'),
    },
    status: {
      search: vocabularySearch(over((row) => row.status), vocabulary.contractorStatus),
      minSearchLength: 1,
      placeholder: t('common.status'),
    },
  };
}

/**
 * A suggestion's second line, rebuilt from the register.
 *
 * The API-side search composes it from the fixture's own trade and phase, which
 * are English words. The row it came from is looked up by the option's value —
 * the supplier's id — so the two halves are resolved rather than the sentence
 * being assembled from strings that only match one language.
 */
function describeSupplier(
  option: RemoteOption,
  vocabulary: FixtureVocabulary,
): string | undefined {
  const row = CONTRACTORS.find((entry) => entry.id === option.value);
  return row
    ? `${vocabulary.trade(row.trade)} · ${vocabulary.phase(row.phase)}`
    : option.description;
}

/**
 * `domain.ts` has no supplier helper, so the four headline figures are derived
 * here — three of them from the register, the fourth from the expense ledger.
 */
const SUPPLIER_TOTALS = {
  count: CONTRACTORS.length,
  contractValue: CONTRACTORS.reduce((sum, row) => sum + row.contractValue, 0),
  paidToDate: CONTRACTORS.reduce((sum, row) => sum + row.paidToDate, 0),
  recordedSpend: EXPENSES.reduce((sum, line) => sum + line.amount, 0),
};

const STATUS_TONE: Record<string, BadgeTone> = {
  Awarded: 'info',
  'In progress': 'info',
  'On hold': 'warning',
  Closed: 'success',
  Disputed: 'error',
};

/**
 * The expense ledger keys vendors as `con-N`, and the register is `CTR-1000` +
 * N — so the join is positional. It is the only join that resolves: the ledger's
 * `vendorName` is a different set of fictional names with no overlap.
 */
function vendorIdFor(row: (typeof CONTRACTORS)[number]): string {
  return `con-${CONTRACTORS.indexOf(row) + 1}`;
}

/**
 * `searchContractors` answers the empty term with nothing at all, so the field
 * would open with no suggestions to choose from. The empty term is served from
 * the option list the fixture already ships; every real term still goes to the
 * API-side search.
 *
 * Either way the suggestion's second line is resolved here, because it names the
 * trade and the phase — the two words in the list a reader is being told.
 */
function supplierSearch(vocabulary: FixtureVocabulary): RemoteSearch {
  return async (term, signal) => {
    const localize = (option: RemoteOption): RemoteOption => ({
      ...option,
      description: describeSupplier(option, vocabulary),
    });

    if (term.trim() !== '') {
      const result = await searchContractors(term, signal);
      return { ...result, options: result.options.map(localize) };
    }

    const suggestions = CONTRACTOR_OPTIONS.slice(0, 25).map(localize);
    return { options: suggestions, truncated: CONTRACTOR_OPTIONS.length > suggestions.length };
  };
}

export function ContractorsPage() {
  const [choice, setChoice] = useState<RemoteOption | null>(null);
  const t = useT();

  /*
     The register's closed vocabularies — a trade, a phase, a contract state —
     resolved for the active locale. Everything the three are fed into is
     memoised on them, and a new `vocabulary` means the language changed, which
     is exactly when the column model and the search sources have to be rebuilt.
  */
  const vocabulary = useFixtureVocabulary();

  const columns = useMemo(() => tableColumns(t, vocabulary), [t, vocabulary]);
  const selectSources = useMemo(() => buildSelectSources(t, vocabulary), [t, vocabulary]);
  const search = useMemo(() => supplierSearch(vocabulary), [vocabulary]);

  const selected = useMemo(
    () => (choice ? CONTRACTORS.find((row) => row.id === choice.value) ?? null : null),
    [choice],
  );

  const spend = useMemo(() => {
    if (!selected) return { lines: [] as typeof EXPENSES, total: 0 };
    const lines = EXPENSES.filter((line) => line.vendorId === vendorIdFor(selected));
    return { lines, total: lines.reduce((sum, line) => sum + line.amount, 0) };
  }, [selected]);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('contractors.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">{t('contractors.subtitle')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('contractors.suppliersOnFile')}
          value={SUPPLIER_TOTALS.count}
          format="number"
          hint={t('contractors.suppliersOnFileHint')}
        />
        <Metric
          label={t('contractors.contractValue')}
          value={SUPPLIER_TOTALS.contractValue}
          format="money"
          hint={t('contractors.contractValueHint')}
        />
        <Metric
          label={t('contractors.paidToDate')}
          value={SUPPLIER_TOTALS.paidToDate}
          format="money"
          hint={t('contractors.paidToDateHint')}
        />
        <Metric
          label={t('contractors.recordedSpend')}
          value={SUPPLIER_TOTALS.recordedSpend}
          format="money"
          tone="info"
          hint={t('contractors.recordedSpendHint')}
        />
      </div>

      <Panel
        title={t('contractors.lookup.title')}
        description={t('contractors.lookup.description')}
        padding="lg"
      >
        <Select
          id="supplier-lookup"
          search={search}
          value={choice}
          onValueChange={setChoice}
          label={t('contractors.supplier')}
          placeholder={t('contractors.lookup.placeholder')}
          initialLoad
          minSearchLength={0}
          hint={t('contractors.lookup.hint')}
        />
      </Panel>

      {selected ? (
        <Panel
          title={selected.name}
          description={t('contractors.detail.description')}
          headerActions={
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral" variant="subtle">
                {vocabulary.trade(selected.trade)}
              </Badge>
              <Badge tone="neutral" variant="subtle">
                {vocabulary.phase(selected.phase)}
              </Badge>
              <Badge tone={STATUS_TONE[selected.status] ?? 'neutral'} variant="subtle">
                {vocabulary.contractorStatus(selected.status)}
              </Badge>
            </div>
          }
        >
          <div className="flex flex-col gap-6">
            <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Stat label={t('contractors.contractId')} value={selected.id} />
              <Stat
                label={t('contractors.contractValue')}
                value={formatMoney(selected.contractValue)}
              />
              <Stat label={t('contractors.paidToDate')} value={formatMoney(selected.paidToDate)} />
              <Stat
                label={t('contractors.outstanding')}
                value={formatMoney(selected.contractValue - selected.paidToDate)}
              />
              <Stat label={t('contractors.crewSize')} value={formatNumber(selected.crewSize)} />
              <Stat
                label={t('contractors.started')}
                value={formatDate(selected.startDate, { style: 'medium' })}
              />
              <Stat
                label={t('contractors.lastInspection')}
                value={formatDate(selected.lastInspection, { style: 'medium' })}
              />
              <Stat
                label={t('contractors.approvedAt')}
                value={formatDateTime(selected.approvedAt)}
              />
              <Stat
                label={t('contractors.verified')}
                value={selected.isVerified ? t('contractors.yes') : t('contractors.no')}
              />
            </dl>

            <Progress
              value={selected.completion}
              tone="primary"
              label={t('contractors.progress', { name: selected.name })}
              showValue
            />

            <div className="flex flex-col gap-3">
              <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
                {t('contractors.recordedSpend')}
              </h3>

              {spend.lines.length === 0 ? (
                <p className="rounded-xs border border-line-subtle bg-surface-muted px-4 py-3 text-sm text-muted">
                  {t('contractors.noSpend', { name: selected.name })}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <caption className="pb-2 text-left text-xs text-muted">
                      {t('contractors.spend.caption', {
                        name: selected.name,
                        count: spend.lines.length,
                        amount: formatMoney(spend.total),
                      })}
                    </caption>
                    <thead>
                      <tr className="border-b border-line">
                        <th
                          scope="col"
                          className="px-2 py-2 text-left font-display text-2xs font-semibold tracking-label text-muted uppercase"
                        >
                          {t('contractors.column.project')}
                        </th>
                        <th
                          scope="col"
                          className="px-2 py-2 text-left font-display text-2xs font-semibold tracking-label text-muted uppercase"
                        >
                          {t('contractors.phase')}
                        </th>
                        <th
                          scope="col"
                          className="px-2 py-2 text-left font-display text-2xs font-semibold tracking-label text-muted uppercase"
                        >
                          {t('common.date')}
                        </th>
                        <th
                          scope="col"
                          className="px-2 py-2 text-right font-display text-2xs font-semibold tracking-label text-muted uppercase"
                        >
                          {t('common.amount')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {spend.lines.map((line) => (
                        <tr key={line.id} className="border-b border-line-subtle">
                          <td className="px-2 py-2 text-body">
                            {findProject(line.projectId)?.name ?? line.projectId}
                          </td>
                          <td className="px-2 py-2 text-body">
                            {vocabulary.phase(line.phaseId)}
                          </td>
                          <td className="px-2 py-2 text-body">
                            {formatDate(line.date, { style: 'medium' })}
                          </td>
                          <td className="px-2 py-2 text-right text-body">
                            {formatMoney(line.amount, { precision: 0 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-line-strong">
                        <th
                          scope="row"
                          colSpan={3}
                          className="px-2 py-2 text-left font-display text-2xs font-semibold tracking-label text-heading uppercase"
                        >
                          {t('contractors.totalRecorded')}
                        </th>
                        <td className="px-2 py-2 text-right font-semibold text-heading">
                          {formatMoney(spend.total, { precision: 0 })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Panel>
      ) : (
        <EmptyState
          variant="page"
          title={t('contractors.empty.title')}
          description={t('contractors.empty.description')}
        />
      )}

      <Panel
        title={t('contractors.register.title')}
        description={t('contractors.register.description')}
        padding="lg"
      >
        <PaginatedTable
          id="supplier-register"
          caption={t('contractors.register.caption')}
          columns={columns}
          dataSource={fetchContractors}
          rowKey="id"
          selectSources={selectSources}
          pageSizeOptions={[10, 25, 50]}
          defaultPageSize={10}
          emptyTitle={t('contractors.register.emptyTitle')}
          errorTitle={t('contractors.register.errorTitle')}
        />
      </Panel>

      <p className="text-xs text-muted">{t('contractors.register.note')}</p>
    </div>
  );
}

/** One labelled figure of the detail panel, so the term/description pair is consistent. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="font-display text-2xs font-semibold tracking-label text-muted uppercase">
        {label}
      </dt>
      <dd className="text-sm text-heading">{value}</dd>
    </div>
  );
}
